import { EditableTitle } from "@/components/shared/EditableTitle";
import { UserPickerSingleSelect } from "@/components/shared/UserPicker";
import { Dropzone } from "@/components/ui/Dropzone";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useChronologyRun } from "@/hooks/cases/useChronologyRun";
import { isRetryableViewerLoadError } from "@/hooks/pdfViewer";
import { useCasePhysician } from "@/hooks/useCasePhysician";
import { usePhysicians } from "@/hooks/usePhysicians";
import { isConflictError } from "@/utils/errorUtils";
import JSZip from "jszip";
import { Download, FileUp, Trash2, Upload, X } from "lucide-react";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useParams } from "react-router-dom";

import { CASE_STATUS, MAX_FILE_SIZE, SUPPORTED_FILE_TYPES } from "../constants";
import { useAuth } from "../contexts/AuthContext";
import { useGetSignedUrl, useSignedUrlWithRetry } from "../hooks/signedUrl";
import { invalidateCaseFilesCache } from "../hooks/useCaseFiles";
import { useCaseService } from "../hooks/useCaseService";
import { useFileUpload } from "../hooks/useFileUpload";
import { useNavigationBlocker } from "../hooks/useNavigationBlocker";
import { usePhysicianTemplate } from "../hooks/usePhysicianTemplate";
import { useFileDatabase } from "../hooks/userFileDatabase";
import { canEditOwnPreference } from "../permissions/preferences";
import { useCanRequestChronologyRun } from "../permissions/v2/cases";
import supabase from "../services/supabaseClient";
import styles from "../styles/FileManagement.module.css";
import { isAdmin, isProfessional as isPhysicianRole } from "../utils";
import { getErrorMessage } from "../utils";
import { mapWithConcurrency } from "../utils/concurrency";
import FileUpdateModal from "./FileManagement/FileUpdateModal";
import { getFileExtension, sanitizeFilename } from "./FileManagement/fileUtils";
import { PdfCheckFailedError, isPasswordProtectedPdf } from "./FileManagement/pdfEncryptionCheck";
import FileSidePanel from "./FileSidePanel";
import { ConcisenessSlider } from "./Templates/ConcisenessSlider";
import StatusBadge from "./shared/StatusBadge";
import { Loading } from "./ui";
import { DateInput } from "./ui";

// The fallback path that buffers a whole file is mutex-guarded inside pdfEncryptionCheck.ts,
// so this only bounds how many cheap range-based checks run in parallel.
const PASSWORD_CHECK_CONCURRENCY_LIMIT = 5;

const FAILED_CATEGORY_STYLE = {
  group: `bg-destructive-subtle border-destructive-border ${styles.failedGroup} text-destructive-text`,
  heading: "text-destructive-text",
  Icon: X,
  iconClassName: "text-destructive",
};

const UNCATEGORIZED_STYLE = {
  group: "bg-surface-sunken border-border text-foreground",
  heading: "text-foreground",
  Icon: FileUp,
  iconClassName: "text-muted-foreground",
};

const FINISHED_CATEGORY_STYLE = {
  group: "bg-success-subtle border-success-border text-success-text",
  heading: "text-success-text",
  Icon: FileUp,
  iconClassName: "text-success",
};

const CATEGORY_STYLES = {
  Uploading: {
    group: "bg-warning-subtle border-warning-border text-warning-text",
    heading: "text-warning-text",
    Icon: Upload,
    iconClassName: "text-warning",
  },
  Processing: {
    group: "bg-info-subtle border-info-border text-info-text",
    heading: "text-info-text",
    Icon: FileUp,
    iconClassName: "text-info",
  },
  "Upload Failed": FAILED_CATEGORY_STYLE,
  Failed: FAILED_CATEGORY_STYLE,
  Imaging: FINISHED_CATEGORY_STYLE,
  "Surgical visits/procedures": FINISHED_CATEGORY_STYLE,
  Therapy: FINISHED_CATEGORY_STYLE,
  "Official reports (police)": FINISHED_CATEGORY_STYLE,
  General: FINISHED_CATEGORY_STYLE,
};

const getCategoryStyle = (categoryName) => CATEGORY_STYLES[categoryName] ?? UNCATEGORIZED_STYLE;

const getUniqueExtensionsLabel = (files) => {
  const extensions = [...new Set(files.map((file) => getFileExtension(file.name)).filter(Boolean))];
  return extensions.join(", ");
};

const buildUnsupportedFileTypeToastMessage = (files) => {
  const fileNames = files.map((file) => file.name).join(", ");
  const extensionsLabel = getUniqueExtensionsLabel(files);

  return `Couldn't upload ${fileNames}. This file type (${extensionsLabel}) is not yet supported.`;
};

const buildPasswordProtectedToastMessage = (files) => {
  const fileNames = files.map((file) => file.name).join(", ");
  const subject = files.length === 1 ? "This file is" : "These files are";

  return `Couldn't upload ${fileNames}. ${subject} password-protected. Please upload an unlocked copy.`;
};

const buildPdfCheckFailedToastMessage = (files) => {
  const fileNames = files.map((file) => file.name).join(", ");
  const subject = files.length === 1 ? "This file" : "These files";

  return `Couldn't upload ${fileNames}. ${subject} failed to open. Please check that the PDF isn't corrupted or password-protected and try again.`;
};

const FileManagement = ({ onCaseNameUpdate }) => {
  const caseServiceV2 = useCaseService();
  const { assignPhysician } = useCasePhysician();
  const { requestChronologyRun, fetchCaseStatus } = useChronologyRun();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { user, firmContext, getFirmRoleData, hasPermission } = useAuth();
  const isProfessional = isPhysicianRole(firmContext);
  const userCanEditOwnPreference = canEditOwnPreference(firmContext);
  const canRequestChronologyRun = useCanRequestChronologyRun(firmContext?.firmId);

  const [caseName, setCaseName] = useState("");
  const [examDate, setExamDate] = useState(undefined);
  const [dueDate, setDueDate] = useState(undefined);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const nonDeletedUploadedFiles = uploadedFiles.filter((file) => !file.is_deleted);
  // const [categories, setCategories] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const redirectTimeoutRef = useRef(null);
  useEffect(() => () => clearTimeout(redirectTimeoutRef.current), []);
  const [updateModal, setUpdateModal] = useState({
    isOpen: false,
    existingFiles: [],
    newFiles: [],
    softDeletedFiles: [],
  });

  const handleUpdateModalClose = () => {
    setUpdateModal({
      isOpen: false,
      existingFiles: [],
      newFiles: [],
      softDeletedFiles: [],
    });
  };

  const handleUpdateModalAction = async (shouldReplaceExisting) => {
    const { existingFiles, newFiles, softDeletedFiles } = updateModal;

    // Close modal and then update the files.
    handleUpdateModalClose();

    if (shouldReplaceExisting) {
      await processFiles([...existingFiles, ...softDeletedFiles, ...newFiles]);
    } else {
      await processFiles([...softDeletedFiles, ...newFiles]);
    }
  };

  // const [hasProducerRole, setHasProducerRole] = useState(false);

  const [caseStatus, setCaseStatus] = useState(0); // Default to draft
  const [caseDetailsLoaded, setCaseDetailsLoaded] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const physicianSaveSeq = useRef(0);

  const [isRushRequest, setIsRushRequest] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingFiles, setIsCheckingFiles] = useState(false);

  const {
    physicians: adminPhysicians,
    isLoading: adminPhysiciansLoading,
    isError: adminPhysiciansError,
    errorMessage: physiciansErrorMessage,
  } = usePhysicians();
  // Non-admins never see the full roster — only the physician already on this case
  // (from `selectedProfessional`, set once case details load).
  const physicians = useMemo(() => {
    if (isAdmin(firmContext)) return adminPhysicians;
    return selectedProfessional?.user_id ? [selectedProfessional] : [];
  }, [firmContext, adminPhysicians, selectedProfessional]);
  const loadingPhysicians = isAdmin(firmContext) ? adminPhysiciansLoading : !caseDetailsLoaded;

  useEffect(() => {
    if (adminPhysiciansError) {
      console.error(physiciansErrorMessage);
      toast.error(physiciansErrorMessage, { id: "physicians-load-error" });
    }
  }, [adminPhysiciansError, physiciansErrorMessage]);

  const [fileMapping, setFileMapping] = useState({});

  const [showRightPanel, setShowRightPanel] = useState(false);
  const [rightPanelWidth] = useState(500);
  const [currentModalFileName, setCurrentModalFileName] = useState("");
  const [currentModalFileId, setCurrentModalFileId] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const { data: physicianTemplateData } = usePhysicianTemplate(
    userCanEditOwnPreference ? selectedProfessional?.user_id : null
  );
  const physicianTemplateId = physicianTemplateData?.template_id ?? null;

  const { uploadFile } = useFileUpload();
  const { saveFileDetails } = useFileDatabase();
  const { getSignedUrl } = useGetSignedUrl();
  const {
    url: fileUrl,
    onViewerLoadError,
    onViewerLoadSuccess,
  } = useSignedUrlWithRetry(currentModalFileId, {
    onUnrecoverableError: () => {
      setShowRightPanel(false);
      setCurrentModalFileId(null);
      toast.error("Failed to open file");
    },
    isRetryableError: isRetryableViewerLoadError,
  });

  const handlePreview = (fileId, bucketUrl, pageNumber = 0) => {
    const fileName = fileMapping[fileId] || bucketUrl.split("/").pop();
    setCurrentModalFileName(fileName);
    setCurrentModalFileId(fileId != null ? String(fileId) : null);
    setPageNumber(pageNumber + 1);
    setShowRightPanel(true);
  };

  const renderRightPanel = () => {
    return (
      <FileSidePanel
        isOpen={showRightPanel}
        onClose={() => {
          setShowRightPanel(false);
          setCurrentModalFileId(null);
        }}
        fileName={currentModalFileName}
        fileId={currentModalFileId ?? undefined}
        fileUrl={fileUrl}
        initialPage={pageNumber - 1}
        initialWidth={rightPanelWidth}
        minWidth={300}
        maxWidth={800}
        onPdfLoadError={onViewerLoadError}
        onPdfLoadSuccess={onViewerLoadSuccess}
      />
    );
  };

  const groupFilesByCategory = () => {
    const groupedFiles = {};
    nonDeletedUploadedFiles.forEach((file) => {
      let categoryName;
      if (file.uploadStatus === "uploading") {
        categoryName = "Uploading";
      } else if (file.uploadStatus === "failed") {
        categoryName = "Upload Failed";
      } else if (file.processing_status === 0) {
        categoryName = "Processing";
      } else if (file.processing_status === 2) {
        categoryName = "Failed";
      } else {
        categoryName = file.case_file_categories?.category_name || "Uncategorized";
      }
      if (!groupedFiles[categoryName]) {
        groupedFiles[categoryName] = {
          key: categoryName,
          categoryName: categoryName,
          files: [],
        };
      }
      groupedFiles[categoryName].files.push(file);
    });
    return groupedFiles;
  };

  const handlePreviewClick = (e, file) => {
    e.preventDefault();
    const isUploadFailed = file.uploadStatus === "failed";
    if (isUploadFailed) {
      toast.error("File upload failed. This file cannot be previewed.");
      return;
    }
    const pageNumber =
      file.page_numbers && file.page_numbers.length > 0
        ? file.page_numbers[0] - 1 // Convert to 0-based index
        : 0;
    handlePreview(file.file_id, file.bucket_url, pageNumber);
  };

  const handleDownloadClick = (file) => {
    const isUploadFailed = file.uploadStatus === "failed";
    if (isUploadFailed) {
      toast.error("File upload failed. This file cannot be downloaded.");
      return;
    }
    downloadFile(file.file_id, file.bucket_url);
  };

  const renderFile = (file) => {
    const isProcessingFailed = file.processing_status === 2;
    const isUploadFailed = file.uploadStatus === "failed";
    const fileName = file.bucket_url.split("/").pop();
    // Colour is inherited so each file reads as part of its category group's tint; the
    // underline is what carries the "this is clickable" affordance instead.
    const fileNameClassName = `min-w-0 flex-1 break-words underline decoration-dotted underline-offset-2 hover:decoration-solid ${
      isProcessingFailed || isUploadFailed
        ? "text-destructive-text cursor-not-allowed"
        : "text-inherit"
    }`;
    return (
      <div key={file.file_id} className="mb-1 flex min-w-0 items-start justify-between gap-2">
        <a
          href="#"
          onClick={(e) => handlePreviewClick(e, file)}
          className={fileNameClassName}
          title={isUploadFailed ? "Upload failed - file not available" : `View "${fileName}"`}
        >
          {fileName}
        </a>
        <div className={`${styles.fileActionRow} flex flex-shrink-0 items-center gap-2 pt-0.5`}>
          <button
            onClick={() => handleDownloadClick(file)}
            className={`${
              isProcessingFailed || isUploadFailed
                ? "cursor-not-allowed text-destructive hover:text-destructive-text"
                : "text-muted-foreground hover:text-foreground"
            }`}
            title={isUploadFailed ? "Upload failed - file not available" : ""}
          >
            <Download size={18} />
          </button>
          <button
            onClick={() => deleteFile(file.file_id)}
            className={`${
              isProcessingFailed || isUploadFailed
                ? "text-destructive hover:text-destructive-text"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    );
  };

  const renderFiles = () => {
    const groupOrder = ["Upload Failed", "Uploading", "Failed", "Processing"];
    const grouped = groupFilesByCategory();
    const sortedEntries = Object.entries(grouped).sort(([a], [b]) => {
      const aIndex = groupOrder.indexOf(a);
      const bIndex = groupOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });

    return sortedEntries.map(([categoryName, category]) => {
      const { group, heading, Icon, iconClassName } = getCategoryStyle(categoryName);
      return (
        <div key={categoryName} className={`mb-4 rounded border p-4 ${group}`}>
          <h3 className={`mb-2 flex items-center font-bold ${heading}`}>
            <Icon className={`${iconClassName} mr-2`} size={20} />
            {category.categoryName}
          </h3>
          {category.files.map((file) => renderFile(file))}
        </div>
      );
    });
  };

  useEffect(() => {
    const checkProducerRole = async () => {
      if (!firmContext?.firmId) return;

      const firmRoleData = await getFirmRoleData(firmContext.firmId);
      if (!firmRoleData) return;

      // const producerRoles = ["admin", "manager", "analyst", "super_admin"];
      // setHasProducerRole(producerRoles.includes(firmRoleData.roleName));
    };

    if (firmContext?.firmId) {
      checkProducerRole();
    }
  }, [firmContext, getFirmRoleData]);

  useEffect(() => {
    const fetchData = async () => {
      if (user && caseId) {
        await fetchCaseDetails();
        fetchCaseFiles();
        updateFileMapping(caseId);
      }
    };

    fetchData();
  }, [caseId, user]);

  const fetchCaseDetails = async () => {
    if (caseId) {
      try {
        const caseData = await caseServiceV2.getCaseDetails(caseId);

        if (caseData) {
          setCaseName(caseData.case_name || "New Case");
          setExamDate(caseData.exam_dt || undefined);
          setDueDate(caseData.due_date || undefined);
          setCaseStatus(caseData.status_id || 0);

          setSelectedProfessional({
            user_id: caseData.physician_id,
            name: caseData.physician_name,
          });

          // Get firm settings from cache if needed
          if (user?.firm_id) {
            const firmRoleData = await getFirmRoleData(user.firm_id);
            if (firmRoleData?.firmSettings) {
              setIsRushRequest(firmRoleData.firmSettings.rush_enabled || false);
            }
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load case details");
      } finally {
        setCaseDetailsLoaded(true);
      }
    }
  };

  const refreshCaseStatus = async () => {
    try {
      setCaseStatus(await fetchCaseStatus(caseId));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCaseFiles = async () => {
    // Fetch ALL files, including deleted ones
    const { data, error } = await supabase
      .from("case_files")
      .select(
        `
        file_id, 
        bucket_url,
        filename,
        category_id,
        is_deleted,
        processing_status,
        case_file_categories (
          category_id,
          category_name
        )
      `
      )
      .eq("case_id", caseId);

    if (error) {
      console.error("Error fetching case files:", error);
    } else {
      setUploadedFiles(data);
    }
  };

  const handleCaseNameSaved = async (newCaseName) => {
    try {
      await caseServiceV2.renameCase(caseId, newCaseName);
      setCaseName(newCaseName);
      onCaseNameUpdate?.(caseId, newCaseName);
    } catch (error) {
      console.error("Error updating case name:", error);
      toast.error("Failed to update case name");
    }
  };

  const handleExamDateChange = async (e) => {
    const newExamDate = e.target.value || undefined;
    setExamDate(newExamDate);

    const { error } = await supabase
      .from("cases")
      .update({ exam_dt: newExamDate })
      .eq("case_id", caseId);

    if (error) {
      console.error("Error updating exam date:", error);
    }
  };

  const handleDueDateChange = async (e) => {
    const newDueDate = e.target.value || undefined;
    setDueDate(newDueDate);

    const { error } = await supabase
      .from("cases")
      .update({ due_date: newDueDate })
      .eq("case_id", caseId);

    if (error) {
      console.error("Error updating due date:", error);
    }
  };

  const checkExistingFile = (fileName) => {
    // Check in all files, including deleted ones
    const existingFile = uploadedFiles.find((file) => {
      const bucketFileName = file.bucket_url.split("/").pop();
      return (
        (file.filename === fileName || bucketFileName === fileName) &&
        !file.file_id.startsWith("temp_")
      );
    });

    return existingFile
      ? {
          file: existingFile,
          isSoftDeleted: existingFile.is_deleted,
        }
      : null;
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!user) {
        return;
      }

      // Sanitize filenames silently - create new File objects with sanitized names
      const sanitizedFiles = acceptedFiles.map((file) => {
        const sanitizedName = sanitizeFilename(file.name);
        if (sanitizedName !== file.name) {
          // Create a new File object with the sanitized name
          return new File([file], sanitizedName, {
            type: file.type,
            lastModified: file.lastModified,
          });
        }
        return file;
      });

      // Validate files
      const supportedFiles = [];
      const unsupportedFiles = [];
      const oversizedFiles = [];

      for (const file of sanitizedFiles) {
        const fileExtension = getFileExtension(file.name);

        // Categorize file based on size and type
        if (file.size > MAX_FILE_SIZE) {
          oversizedFiles.push(file);
        } else if (Object.values(SUPPORTED_FILE_TYPES).flat().includes(fileExtension)) {
          supportedFiles.push(file);
        } else {
          unsupportedFiles.push(file);
        }
      }

      // Show error messages for unsupported/oversized files
      if (unsupportedFiles.length > 0) {
        toast.error(buildUnsupportedFileTypeToastMessage(unsupportedFiles));
      }

      if (oversizedFiles.length > 0) {
        const oversizedFileNames = oversizedFiles.map((file) => file.name).join(", ");
        toast.error(
          `We couldn't upload: ${oversizedFileNames}. We currently only support up to 2GB per file upload. If you'd like to enable larger file uploads, please contact us at support@paribus.ai.`
        );
      }

      let passwordCheckResults = [];
      if (supportedFiles.length > 0) {
        setIsCheckingFiles(true);
        try {
          passwordCheckResults = await mapWithConcurrency(
            supportedFiles,
            PASSWORD_CHECK_CONCURRENCY_LIMIT,
            async (file) => {
              try {
                const isPasswordProtected = await isPasswordProtectedPdf(file);
                return { file, status: isPasswordProtected ? "protected" : "uploadable" };
              } catch (error) {
                // A corrupt/unreadable PDF leaves protection status unknown, so it's
                // held back with its own message instead of silently uploading it.
                if (error instanceof PdfCheckFailedError) return { file, status: "check-failed" };
                throw error;
              }
            }
          );
        } finally {
          setIsCheckingFiles(false);
        }
      }
      const passwordProtectedFiles = passwordCheckResults
        .filter((result) => result.status === "protected")
        .map((result) => result.file);
      const checkFailedFiles = passwordCheckResults
        .filter((result) => result.status === "check-failed")
        .map((result) => result.file);
      const uploadableFiles = passwordCheckResults
        .filter((result) => result.status === "uploadable")
        .map((result) => result.file);

      // One toast per file so every file is visible, not just one truncated, joined message.
      passwordProtectedFiles.forEach((file) => {
        toast.error(buildPasswordProtectedToastMessage([file]));
      });

      checkFailedFiles.forEach((file) => {
        toast.error(buildPdfCheckFailedToastMessage([file]));
      });

      if (uploadableFiles.length === 0) {
        return; // No supported files to process
      }

      // Check for existing files
      const existingFiles = [];
      const newFiles = [];
      const softDeletedFiles = [];

      for (const file of uploadableFiles) {
        const existingFileInfo = checkExistingFile(file.name);
        if (existingFileInfo) {
          if (existingFileInfo.isSoftDeleted) {
            softDeletedFiles.push({ file: existingFileInfo.file, newFile: file });
          } else {
            existingFiles.push({ file: existingFileInfo.file, newFile: file });
          }
        } else {
          newFiles.push({ file: null, newFile: file });
        }
      }

      if (existingFiles.length > 0) {
        setUpdateModal({
          isOpen: true,
          existingFiles: existingFiles,
          newFiles: newFiles,
          softDeletedFiles: softDeletedFiles,
        });
        return;
      }

      await processFiles([...softDeletedFiles, ...newFiles]);
    },
    [caseId, user, uploadedFiles]
  );

  const onDropRejected = useCallback((fileRejections) => {
    const icloudRejections = fileRejections.filter((rejection) =>
      rejection.file.name.endsWith(".icloud")
    );
    const otherRejections = fileRejections.filter(
      (rejection) => !rejection.file.name.endsWith(".icloud")
    );

    if (icloudRejections.length > 0) {
      const icloudFileNames = icloudRejections.map((rejection) => rejection.file.name).join(", ");
      toast.error(
        `Couldn't upload ${icloudFileNames} because it is in iCloud. Download it to your device first (for example, to the desktop) then try uploading.`
      );
    }

    if (otherRejections.length > 0) {
      toast.error(
        buildUnsupportedFileTypeToastMessage(otherRejections.map((rejection) => rejection.file))
      );
    }
  }, []);

  const downloadFile = async (fileId, bucketUrl) => {
    try {
      const { signed_url: signedUrl } = await getSignedUrl(String(fileId));

      // Fetch the file data as a blob
      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error("Failed to download file");
      }
      const blob = await response.blob();

      // Create a blob URL and trigger download
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = bucketUrl.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  const deleteFile = async (fileId) => {
    if (fileId.startsWith("temp_")) {
      // Upload may still be in flight. Remove the placeholder from the UI now, but
      // remember the id so that if the upload finishes anyway, we soft-delete the
      // resulting DB row instead of silently re-adding it (see processFiles).
      pendingTempDeletesRef.current.add(fileId);
      setUploadedFiles((prevFiles) => prevFiles.filter((file) => file.file_id !== fileId));
      return;
    } else {
      const { data, error } = await supabase
        .from("case_files")
        .update({ is_deleted: true })
        .eq("file_id", fileId);

      if (error) {
        console.error("Error marking file as deleted:", error);
        return;
      }

      try {
        await caseServiceV2.deleteFileEmbeddedImages(caseId, fileId);
      } catch (embeddedImageDeleteError) {
        console.error("Error deleting embedded images for file:", embeddedImageDeleteError);
      }

      invalidateCaseFilesCache(caseId);

      console.log("File marked as deleted:", data);
    }

    setUploadedFiles((prevFiles) =>
      prevFiles.map((file) => (file.file_id === fileId ? { ...file, is_deleted: true } : file))
    );
  };

  const uploadedFilesRef = useRef(uploadedFiles);
  uploadedFilesRef.current = uploadedFiles;

  // Temp (placeholder) file ids whose in-flight upload was deleted before it finished.
  const pendingTempDeletesRef = useRef(new Set());

  const fetchFileStatuses = useCallback(async (fileIds) => {
    const validFileIds = fileIds.filter((fileId) => !fileId.startsWith("temp_"));
    if (validFileIds.length === 0) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from("case_files")
        .select("file_id, category_id, processing_status, case_file_categories(category_name)")
        .in("file_id", validFileIds);

      if (error) throw error;
      return data ?? [];
    } catch (error) {
      console.error("Error fetching file statuses:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!isPolling) return;

    let intervalId;

    const pollFileStatus = async () => {
      const processingFiles = uploadedFilesRef.current.filter(
        (file) =>
          file.processing_status === 0 && !file.file_id.startsWith("temp_") && !file.is_deleted
      );

      if (processingFiles.length === 0) {
        setIsPolling(false);
        return;
      }

      const statuses = await fetchFileStatuses(processingFiles.map((file) => file.file_id));
      const statusByFileId = Object.fromEntries(statuses.map((status) => [status.file_id, status]));

      const hasUpdates = processingFiles.some(
        (file) => statusByFileId[file.file_id]?.processing_status !== 0
      );

      if (!hasUpdates) {
        return;
      }

      setUploadedFiles((prevFiles) =>
        prevFiles.map((file) => {
          const status = statusByFileId[file.file_id];
          if (status && status.processing_status !== 0) {
            return { ...file, ...status };
          }
          return file;
        })
      );
    };

    void pollFileStatus();
    intervalId = window.setInterval(pollFileStatus, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchFileStatuses, isPolling]);

  const processFiles = async (fileItems) => {
    /*
    fileItems is an array of { file: existingFile, newFile: newFile }
    if the file is null, then it is a new file.
    */

    const placeholderFiles = [];

    setUploadedFiles((previousFiles) => {
      const updatedFiles = [...previousFiles];

      fileItems.forEach((fileItem) => {
        const { file: existingFile, newFile } = fileItem;
        let currentFile;

        if (existingFile !== null) {
          // For updates, return the updated existing file with uploading status
          currentFile = {
            ...existingFile,
            processing_status: 0,
            is_deleted: false,
            case_file_categories: { category_name: "Uploading" },
            uploadStatus: "uploading",
          };

          const fileIndex = updatedFiles.findIndex((file) => file.file_id === existingFile.file_id);
          updatedFiles[fileIndex] = currentFile;
        } else {
          // For new uploads, create placeholder
          currentFile = {
            file_id: `temp_${Date.now()}_${newFile.name}`,
            bucket_url: `cases/${caseId}/${newFile.name}`,
            category_id: 0,
            case_file_categories: { category_name: "Uploading" },
            uploadStatus: "uploading",
          };

          updatedFiles.push(currentFile);
        }

        placeholderFiles.push(currentFile);
      });

      return updatedFiles;
    });

    // Process each file (upload or update)
    const fileProcessingPromises = fileItems.map(async (fileItem, index) => {
      const { file: existingFile, newFile } = fileItem;
      const isUpdate = existingFile !== null;

      try {
        const filePath = isUpdate ? existingFile.bucket_url : `cases/${caseId}/${newFile.name}`;

        const cloudFrontUrl = await uploadFile({
          file: newFile,
          filePath,
          isUpdate,
        });

        const fileData = await saveFileDetails({
          file: newFile,
          bucketUrl: filePath,
          cloudFrontUrl,
          existingFileId: isUpdate ? existingFile.file_id : null,
          caseId,
        });

        // If the user deleted this file while the upload was still in flight, the
        // placeholder is already gone from the UI but the DB row was just created.
        // Soft-delete it immediately instead of re-adding it to the list.
        const placeholderId = placeholderFiles[index].file_id;
        if (!isUpdate && pendingTempDeletesRef.current.has(placeholderId)) {
          pendingTempDeletesRef.current.delete(placeholderId);
          await deleteFile(fileData.file_id);
          return fileData;
        }

        // Updating the uploaded files array with the new file data.
        setUploadedFiles((previousFiles) =>
          previousFiles.map((file) => {
            if (
              file.file_id === fileData.file_id ||
              file.file_id === placeholderFiles[index].file_id
            ) {
              return { ...fileData, uploadStatus: "uploaded" };
            } else {
              return file;
            }
          })
        );

        return fileData;
      } catch (error) {
        console.error(`Error processing file ${newFile.name}:`, error);

        /* 
        Mark the file as failed instead of removing it from the UI.
        - If it is an update, restore the original file.
        - If it is a new file, mark the placeholder as failed.
        */
        setUploadedFiles((previousFiles) => {
          if (isUpdate) {
            return previousFiles.map((file) =>
              file.file_id === existingFile.file_id ? existingFile : file
            );
          } else {
            return previousFiles.map((file) => {
              if (file.file_id === placeholderFiles[index].file_id) {
                return {
                  ...file,
                  uploadStatus: "failed",
                  case_file_categories: { category_name: "Upload Failed" },
                };
              }
              return file;
            });
          }
        });

        return null;
      }
    });

    const processingResults = await Promise.all(fileProcessingPromises);

    const failedFiles = processingResults.filter((result) => result === null);
    if (failedFiles.length > 0) {
      toast.error(`${failedFiles.length} file(s) failed to process. Please try again.`);
    }

    // Start polling only after uploads resolve, so the immediate poll runs.
    if (processingResults.length > failedFiles.length) {
      setIsPolling(true);
    }
  };

  const handleSelectPhysician = async (userId) => {
    const previous = selectedProfessional;
    const requestId = ++physicianSaveSeq.current;

    const found = physicians.find((p) => p.user_id === userId);
    setSelectedProfessional(found ? { user_id: found.user_id, name: found.name } : null);

    try {
      const response = await assignPhysician(caseId, userId);
      // Confirm from the server response in case the name differs from the local list.
      if (requestId === physicianSaveSeq.current) {
        setSelectedProfessional({ user_id: response.physician_id, name: response.physician_name });
      }
    } catch (error) {
      // Only the newest selection owns the state — reverting on a stale failure would
      // discard a later pick that succeeded. UserPickerSingleSelect toasts what we rethrow.
      if (requestId === physicianSaveSeq.current) {
        setSelectedProfessional(previous);
      }
      throw error;
    }
  };

  const handleRequestReview = async () => {
    if (!hasUploadedFile) {
      toast.error("Please upload at least one file before requesting a review.");
      return;
    }

    if (hasUploadFailedFile) {
      toast.error("Remove the file(s) that failed to upload before requesting an AI review.");
      return;
    }

    if (!firmContext?.firmId) {
      toast.error("No firm context found. Please try logging out and back in.");
      return;
    }

    // Start loading state
    setIsSubmitting(true);

    try {
      // Physicians are assigned to their own case at creation (best-effort, may have
      // failed); everyone else picks one, and that pick is persisted by the picker itself
      // rather than here.
      if (!isProfessional && !selectedProfessional?.user_id) {
        toast.error("Please select a physician before requesting a review.");
        return;
      }

      await requestChronologyRun(caseId, isRushRequest);

      setCaseStatus(CASE_STATUS.AI_REVIEW);
      setShowSuccessModal(true);
      redirectTimeoutRef.current = setTimeout(() => {
        setShowSuccessModal(false);
        navigate("/cases");
      }, 2000);
    } catch (error) {
      if (error.message === "Session expired") {
        return;
      }
      if (isConflictError(error)) {
        toast(getErrorMessage(error), { icon: "ℹ️" });
        await refreshCaseStatus();
        return;
      }
      console.error("Error requesting review:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFileMapping = async (caseId) => {
    try {
      const { data, error } = await supabase
        .from("case_files")
        .select("file_id, bucket_url")
        .eq("case_id", caseId);

      if (error) {
        console.error("Error fetching file mapping:", error);
        return;
      }

      const newMapping = data.reduce((acc, file) => {
        const fileName = file.bucket_url ? file.bucket_url.split("/").pop() : "Unknown file";
        acc[file.file_id] = fileName;
        return acc;
      }, {});

      setFileMapping(newMapping);
    } catch (error) {
      console.error("Error updating file mapping:", error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // Check if case is in AI Review and user is not super admin
      if (caseStatus === 1 && !hasPermission("super_admin")) {
        toast.error("Only super admins can move cases out of AI review");
        return;
      }

      // Additional permission checks for specific statuses
      if (newStatus === 4 && !hasPermission("case_actions", "finalize")) {
        toast.error("You do not have permission to finalize cases");
        return;
      }

      await caseServiceV2.updateCaseStatus(caseId, newStatus);
      setCaseStatus(newStatus);
    } catch (error) {
      console.error("Error updating case status:", error);
      toast.error("Failed to update case status");
    }
  };

  const downloadAllFiles = async () => {
    // Filter out failed upload files (they don't exist in backend)
    const downloadableFiles = nonDeletedUploadedFiles.filter(
      (file) => file.uploadStatus !== "failed"
    );

    if (downloadableFiles.length === 0) {
      toast.error("No files to download");
      return;
    }

    try {
      const zip = new JSZip();

      // Show loading toast
      const loadingToast = toast.loading("Preparing files for download...");

      // Download all files and add them to the zip
      const downloadPromises = downloadableFiles.map(async (file) => {
        try {
          const { signed_url: signedUrl } = await getSignedUrl(String(file.file_id));

          const response = await fetch(signedUrl);
          if (!response.ok) {
            throw new Error("Failed to download file");
          }

          const blob = await response.blob();
          const fileName = file.bucket_url.split("/").pop();
          zip.file(fileName, blob);
        } catch (error) {
          console.error(`Error downloading file ${file.bucket_url}:`, error);
          throw error;
        }
      });

      await Promise.all(downloadPromises);

      // Generate the zip file
      const content = await zip.generateAsync({ type: "blob" });

      // Create download link
      const downloadUrl = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${caseName || "case"}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      // Close loading toast and show success
      toast.dismiss(loadingToast);
      toast.success("Files downloaded successfully");
    } catch (error) {
      console.error("Error downloading files:", error);
      toast.error("Failed to download files");
    }
  };

  const isUploading = useMemo(
    () => nonDeletedUploadedFiles.some((f) => f.uploadStatus === "uploading"),
    [uploadedFiles]
  );

  const hasUploadFailedFile = useMemo(
    () => nonDeletedUploadedFiles.some((f) => f.uploadStatus === "failed"),
    [uploadedFiles]
  );

  const hasUploadedFile = nonDeletedUploadedFiles.length > 0;

  const isProcessing = useMemo(
    () => isSubmitting || isUploading || isCheckingFiles,
    [isSubmitting, isUploading, isCheckingFiles]
  );

  const canRequestReview =
    caseStatus === 0 &&
    !isProcessing &&
    !hasUploadFailedFile &&
    hasUploadedFile &&
    Boolean(selectedProfessional?.user_id);

  useNavigationBlocker(isUploading, {
    onBlock: () => {
      toast.error("Please wait for all files to finish uploading before leaving this page.");
    },
  });

  return (
    <div className={`${styles.fileManagement} relative flex flex-1 bg-surface-sunken p-8`}>
      <div className={`mx-auto w-full max-w-4xl ${showRightPanel ? styles.withRightPanel : ""}`}>
        <div className="mb-6 rounded-lg bg-card p-6 shadow">
          <div className="flex items-center gap-3">
            <EditableTitle
              value={caseName}
              onSave={handleCaseNameSaved}
              as="h2"
              sizeClassName="text-2xl"
              placeholder="New Case"
              editAriaLabel="Edit case name"
              wrapperClassName="flex min-w-0 items-center gap-2"
            />
            <StatusBadge status={caseStatus} onStatusChange={handleStatusChange} />
          </div>
        </div>

        <div className="mb-6 rounded-lg bg-card p-6 shadow">
          <Dropzone
            onFiles={onDrop}
            onReject={onDropRejected}
            accept={SUPPORTED_FILE_TYPES}
            multiple
            hint="Drop files here or click to select"
            variant="full"
            className="mb-6"
          />

          {nonDeletedUploadedFiles.length > 0 && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-bold">Uploaded files</h2>
                <button
                  onClick={downloadAllFiles}
                  className="flex items-center justify-center rounded-lg bg-info-subtle px-4 py-2 text-info-text transition duration-200 hover:bg-info-subtle-strong"
                >
                  <Download size={20} className="mr-2" />
                  Download All
                </button>
              </div>
              {renderFiles()}
            </>
          )}
        </div>

        <div className="mx-auto max-w-[640px] rounded-lg bg-card p-6 shadow">
          <h3 className="mb-4 text-center text-lg font-semibold">Request Review</h3>
          <div className="mb-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Review for:
                  <span className="text-destructive" aria-hidden="true">
                    {" *"}
                  </span>
                </span>
                {isAdmin(firmContext) ? (
                  loadingPhysicians ? (
                    <span className="text-sm text-text-disabled">Loading physicians...</span>
                  ) : (
                    <UserPickerSingleSelect
                      users={physicians}
                      value={selectedProfessional?.user_id ? selectedProfessional : null}
                      onSelect={handleSelectPhysician}
                      addLabel="Select physician"
                      emptyText="No physicians available"
                      isEditable={caseStatus === 0}
                    />
                  )
                ) : (
                  <span
                    className={`text-sm ${selectedProfessional?.name ? "" : "text-muted-foreground"}`}
                  >
                    {selectedProfessional?.name ||
                      (caseDetailsLoaded
                        ? "No physician assigned — ask an admin to assign one"
                        : "Loading...")}
                  </span>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:gap-6">
                <div className="w-full sm:w-auto">
                  <label
                    htmlFor="examDate"
                    className="mb-1 block text-sm font-medium text-muted-foreground"
                  >
                    Exam Date
                  </label>
                  <DateInput
                    id="examDate"
                    value={examDate}
                    onChange={(val) => handleExamDateChange({ target: { value: val } })}
                    className="w-full cursor-pointer rounded-md border border-input p-2 pr-10 shadow-sm focus-visible:shadow-focus sm:w-48"
                  />
                </div>

                <div className="w-full sm:w-auto">
                  <label
                    htmlFor="dueDate"
                    className="mb-1 block text-sm font-medium text-muted-foreground"
                  >
                    Due Date
                  </label>
                  <DateInput
                    id="dueDate"
                    value={dueDate || ""}
                    onChange={(val) => handleDueDateChange({ target: { value: val } })}
                    className="w-full cursor-pointer rounded-md border border-input p-2 pr-10 shadow-sm focus-visible:shadow-focus sm:w-48"
                  />
                </div>

                <div className="flex items-center sm:mt-6">
                  <label
                    className={`flex cursor-pointer items-center rounded-full px-4 py-2 transition-colors duration-200 ${isRushRequest ? "bg-destructive-subtle hover:bg-destructive-subtle-strong" : "bg-surface-sunken hover:bg-border"} `}
                  >
                    <input
                      type="checkbox"
                      checked={isRushRequest}
                      onChange={(e) => setIsRushRequest(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`mr-2 flex h-5 w-5 items-center justify-center rounded border-2 ${isRushRequest ? "border-destructive bg-destructive" : "border-border-strong bg-card"} `}
                    >
                      {isRushRequest && (
                        <svg
                          className="h-3 w-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                    <span
                      className={`text-sm font-medium ${isRushRequest ? "text-destructive-text" : "text-muted-foreground"}`}
                    >
                      Rush Request
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          {userCanEditOwnPreference && (
            <div className="mb-6 mt-8">
              {physicianTemplateId ? (
                <ConcisenessSlider templateId={physicianTemplateId} showExample={false} />
              ) : (
                <div aria-busy="true" aria-label="Loading conciseness controls">
                  <Skeleton height={20} borderRadius={8} containerClassName="block w-full" />
                </div>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                0 = highly detailed, 100 = very concise
              </p>
            </div>
          )}

          {hasUploadFailedFile && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive-border bg-destructive-subtle px-3 py-2 text-sm text-destructive-text">
              <X size={16} className="mt-0.5 shrink-0" />
              <span>Some files failed to upload. Remove them to enable the AI review.</span>
            </div>
          )}

          {canRequestChronologyRun && (
            <button
              className={`mt-4 flex w-full items-center justify-center rounded-lg px-4 py-2 ${
                canRequestReview
                  ? "bg-primary text-white hover:bg-primary-hover"
                  : "cursor-not-allowed bg-surface-sunken text-muted-foreground"
              }`}
              onClick={handleRequestReview}
              disabled={!canRequestReview}
            >
              {caseStatus === 1 ? (
                "Review in progress..."
              ) : (
                <>
                  {isProcessing && (
                    <span className="mr-3">
                      <Loading size={20} tone="muted" fullScreen={false} />
                    </span>
                  )}
                  Request Records Review
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {renderRightPanel()}

      <FileUpdateModal
        isOpen={updateModal.isOpen}
        existingFiles={updateModal.existingFiles}
        newFiles={updateModal.newFiles}
        softDeletedFiles={updateModal.softDeletedFiles}
        onCancel={handleUpdateModalClose}
        onUploadNew={() => handleUpdateModalAction(false)}
        onUploadAll={() => handleUpdateModalAction(true)}
      />

      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="max-w-md text-center" hideCloseButton>
          <DialogHeader className="text-center sm:text-center">
            <DialogTitle>Processing Request</DialogTitle>
            <DialogDescription>
              Submitting your review request. You&#39;ll be redirected to the cases page shortly.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};

FileManagement.propTypes = {
  onCaseNameUpdate: PropTypes.func,
};

export default FileManagement;
